import {
  forceTelegramBottomBarColor,
  preemptTelegramBottomBarForTabbarShow,
  scheduleTelegramChromeColors,
  syncTelegramChromeAfterTabbarTransition
} from '../core/telegram.js';
import { getIsUpgradeMode, setIsUpgradeMode } from './app-state.js';
import {
  cancelChatSmoothScrollRaf,
  setOwnerChatAutoScrollEnabled
} from '../features/owner-chat/ui-state.js';
import { scheduleChatKeyboardInset } from './chat-keyboard.js';
import { hideCartPromoBarNow, refreshCartPromoBar } from '../features/cart/cart.js';
import { refreshCheckoutOrderSummary } from '../features/checkout/summary.js';
import { applyCheckoutModeUi } from '../features/checkout/mode.js';
import { refreshBuildIntroCarousel } from '../features/build/intro.js';
import { updateProfileView } from '../features/profile/view.js';
import { prepareOrdersListLoadingUi } from '../features/orders/list.js';
import { bindPortfolioFilterBar, loadPublicPortfolio } from '../features/portfolio/list.js';
import {
  initOwnerChatList,
  initOwnerChatThread,
  stopOwnerChatPolling
} from '../features/owner-chat/chat.js';
import { initAiAssistantView } from '../features/ai/chat.js';
import { initOwnerInfraStatusView, stopOwnerInfraStatusPolling } from '../features/infra-status/owner.js';
import { forceClosePhotoModal } from './photo-modal.js';
import { isCustomerPortfolioLive } from './feature-flags.js';
import { openBetaWipSheet } from './beta-wip.js';

type ViewDirection = 'right' | 'left' | undefined;

interface CheckoutViewElement extends HTMLElement {
  _checkoutStuckUpdate?: () => void;
}

const TABBAR_VIEWS = ['view-main', 'view-owner-presets', 'view-portfolio', 'view-profile'];

function resetActiveViewScroll(view: HTMLElement): void {
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  } catch {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* ignore */
    }
  }
  try {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  } catch {
    /* ignore */
  }
  const app = document.querySelector('.app');
  if (app instanceof HTMLElement) app.scrollTop = 0;
  view.querySelectorAll<HTMLElement>('.page-content, .app-main, .checkout-content, .chat-view').forEach((el) => {
    el.scrollTop = 0;
  });
}

function syncChatHeaderSlot(): void {
  try {
    const view = document.querySelector('#view-consult.view-active, #view-ai-assistant.view-active');
    if (!view) return;
    const header = view.querySelector('.page-header');
    if (!header) return;
    const bottom = Math.ceil(header.getBoundingClientRect().bottom);
    if (bottom > 0) {
      document.documentElement.style.setProperty('--chat-header-slot', bottom + 'px');
    }
  } catch {
    /* ignore */
  }
}

(function initChatHeaderSlotSync(): void {
  function sched(): void {
    requestAnimationFrame(syncChatHeaderSlot);
  }
  window.addEventListener('resize', sched, { passive: true });
  window.addEventListener('orientationchange', () => {
    setTimeout(sched, 80);
    setTimeout(sched, 320);
  });
  if (typeof ResizeObserver !== 'undefined') {
    ['view-consult', 'view-ai-assistant'].forEach((id) => {
      const view = document.getElementById(id);
      if (!view) return;
      const header = view.querySelector('.page-header');
      if (!header) return;
      try {
        new ResizeObserver(sched).observe(header);
      } catch {
        /* ignore */
      }
    });
  }
})();

export function showView(viewId: string, direction?: ViewDirection): void {
  const target = document.getElementById(viewId);
  const current = document.querySelector('.view-active') as HTMLElement | null;
  if (!target || !current || target === current) return;
  if (viewId === 'view-profile') {
    try {
      prepareOrdersListLoadingUi();
    } catch {
      /* ignore */
    }
  }
  const activeView = current;
  const nextView = target;
  if (activeView.id === 'view-presets' && nextView.id !== 'view-presets') {
    try {
      hideCartPromoBarNow();
    } catch {
      /* ignore */
    }
  }
  const shouldShowMainBg =
    viewId === 'view-main' || viewId === 'view-consult' || viewId === 'view-ai-assistant';
  const isAiChatView = viewId === 'view-ai-assistant';
  const isOwnerChatView = viewId === 'view-consult';

  const appRoot = document.querySelector('.app');
  const enteringChat = isOwnerChatView || isAiChatView;
  const leavingChat = activeView.id === 'view-consult' || activeView.id === 'view-ai-assistant';
  const leavingBuildSuccess = activeView.id === 'view-build-success';
  const applyChatClassesNow = !(leavingChat && !enteringChat);
  const tabbar = document.querySelector('.app-tabbar');
  const targetShowsTabbar = TABBAR_VIEWS.indexOf(viewId) !== -1;
  const deferTabbarSync =
    (leavingChat && !enteringChat) || (leavingBuildSuccess && targetShowsTabbar);

  if (appRoot && applyChatClassesNow) {
    appRoot.classList.toggle('app--chat-open', enteringChat);
  }
  if (document.documentElement && applyChatClassesNow) {
    document.documentElement.classList.toggle('html--chat-open', enteringChat);
  }
  if (tabbar && !deferTabbarSync) {
    const shouldShow = TABBAR_VIEWS.indexOf(viewId) !== -1;
    const wasHidden = tabbar.classList.contains('app-tabbar-hidden');
    if (shouldShow && wasHidden) {
      forceTelegramBottomBarColor('#1c1c1e');
    }
    tabbar.classList.toggle('app-tabbar-hidden', !shouldShow);
    const nowHidden = tabbar.classList.contains('app-tabbar-hidden');
    if (!wasHidden && nowHidden) {
      syncTelegramChromeAfterTabbarTransition(tabbar);
    } else {
      scheduleTelegramChromeColors();
    }
  } else if (!deferTabbarSync) {
    scheduleTelegramChromeColors();
  }

  let leaveClass = 'view-leaving';
  let enterClass = 'view-enter';
  if (direction === 'right') {
    leaveClass = 'view-leave-left';
    enterClass = 'view-enter-right';
  } else if (direction === 'left') {
    leaveClass = 'view-leave-right';
    enterClass = 'view-enter-left';
  }

  activeView.classList.remove('view-enter', 'view-enter-right', 'view-enter-left');
  activeView.classList.add(leaveClass);

  let leaveDone = false;
  function completeLeave(): void {
    if (leaveDone) return;
    leaveDone = true;
    try {
      forceClosePhotoModal();
    } catch {
      /* ignore */
    }
    activeView.classList.remove('view-active', 'view-leaving', 'view-leave-left', 'view-leave-right');

    if (!applyChatClassesNow) {
      if (appRoot) appRoot.classList.toggle('app--chat-open', enteringChat);
      if (document.documentElement) {
        document.documentElement.classList.toggle('html--chat-open', enteringChat);
      }
    }
    scheduleChatKeyboardInset();

    if (deferTabbarSync && tabbar) {
      const shouldShow = TABBAR_VIEWS.indexOf(viewId) !== -1;
      const wasHidden = tabbar.classList.contains('app-tabbar-hidden');
      if (shouldShow && wasHidden) {
        forceTelegramBottomBarColor('#1c1c1e');
      }
      tabbar.classList.toggle('app-tabbar-hidden', !shouldShow);
      const nowHidden = tabbar.classList.contains('app-tabbar-hidden');
      if (!wasHidden && nowHidden) {
        syncTelegramChromeAfterTabbarTransition(tabbar);
      } else {
        scheduleTelegramChromeColors();
      }
    }

    document.querySelectorAll('.view.view-active').forEach((v) => {
      if (v !== nextView) {
        v.classList.remove(
          'view-active',
          'view-leaving',
          'view-leave-left',
          'view-leave-right',
          'view-enter',
          'view-enter-right',
          'view-enter-left'
        );
      }
    });
    nextView.classList.add('view-active', enterClass);
    resetActiveViewScroll(nextView);
    try {
      requestAnimationFrame(() => {
        resetActiveViewScroll(nextView);
      });
    } catch {
      /* ignore */
    }
    try {
      refreshCartPromoBar();
    } catch {
      /* ignore */
    }
    if (viewId === 'view-checkout') {
      try {
        applyCheckoutModeUi();
      } catch {
        /* ignore */
      }
      try {
        requestAnimationFrame(() => {
          const v = document.getElementById('view-checkout') as CheckoutViewElement | null;
          v?._checkoutStuckUpdate?.();
        });
      } catch {
        /* ignore */
      }
    }
    if (viewId === 'view-build') {
      try {
        refreshBuildIntroCarousel();
      } catch {
        /* ignore */
      }
    }
    try {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            document.body.classList.toggle('bg-main-active', shouldShowMainBg);
          } catch {
            /* ignore */
          }
        });
      });
    } catch {
      /* ignore */
    }
    if (viewId === 'view-consult') {
      try {
        initOwnerChatThread();
      } catch {
        /* ignore */
      }
      try {
        requestAnimationFrame(syncChatHeaderSlot);
      } catch {
        /* ignore */
      }
    } else if (viewId === 'view-ai-assistant') {
      try {
        initAiAssistantView();
      } catch {
        /* ignore */
      }
      try {
        requestAnimationFrame(syncChatHeaderSlot);
      } catch {
        /* ignore */
      }
    } else if (viewId === 'view-owner-chat-list') {
      try {
        initOwnerChatList();
      } catch {
        /* ignore */
      }
    } else if (viewId === 'view-owner-infra-status') {
      try {
        initOwnerInfraStatusView();
      } catch {
        /* ignore */
      }
    } else if (viewId === 'view-profile') {
      try {
        updateProfileView();
      } catch {
        /* ignore */
      }
    } else {
      try {
        stopOwnerChatPolling();
      } catch {
        /* ignore */
      }
      try {
        stopOwnerInfraStatusPolling();
      } catch {
        /* ignore */
      }
    }
    nextView.addEventListener('animationend', function h(ev: AnimationEvent) {
      if (ev?.target !== nextView) return;
      nextView.removeEventListener('animationend', h);
      nextView.classList.remove('view-enter', 'view-enter-right', 'view-enter-left');
    });
  }

  function onLeaveAnimationEnd(ev: AnimationEvent): void {
    if (ev?.target !== activeView) return;
    activeView.removeEventListener('animationend', onLeaveAnimationEnd);
    completeLeave();
  }
  activeView.addEventListener('animationend', onLeaveAnimationEnd);
  setTimeout(completeLeave, 450);
}

try {
  document.body.classList.remove('bg-main-active');
  document.body.classList.remove('bg-main-intro');
  const activeView = document.querySelector('.view-active');
  const shouldShowMainBg =
    !!activeView &&
    (activeView.id === 'view-main' ||
      activeView.id === 'view-consult' ||
      activeView.id === 'view-ai-assistant');
  if (shouldShowMainBg) {
    try {
      document.body.classList.add('bg-main-intro');
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      try {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try {
              document.body.classList.add('bg-main-active');
            } catch {
              /* ignore */
            }
          });
        });
      } catch {
        /* ignore */
      }
    }, 220);
    setTimeout(() => {
      try {
        document.body.classList.remove('bg-main-intro');
      } catch {
        /* ignore */
      }
    }, 17000);
  }
} catch {
  /* ignore */
}

(function initViewMainEnterCleanup(): void {
  const main = document.getElementById('view-main');
  if (!main) return;
  const mainView = main;
  function done(): void {
    mainView.classList.remove('view-enter', 'view-enter-right', 'view-enter-left');
  }
  mainView.addEventListener(
    'animationend',
    (e: AnimationEvent) => {
      if (e?.target !== mainView) return;
      done();
    },
    { once: true }
  );
  setTimeout(done, 400);
})();

document.querySelectorAll('.back-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    let target = (btn as HTMLElement).dataset.backTo || 'view-main';

    let leavingConsult = false;
    try {
      const cur = document.querySelector('.view-active');
      if (cur?.id === 'view-consult') {
        leavingConsult = true;
        setOwnerChatAutoScrollEnabled(false);
        try {
          cancelChatSmoothScrollRaf();
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }

    if (getIsUpgradeMode() && target === 'view-build') {
      target = 'view-main';
      setIsUpgradeMode(false);
    }
    if (
      target === 'view-main' ||
      target === 'view-owner-presets' ||
      target === 'view-portfolio' ||
      target === 'view-profile'
    ) {
      preemptTelegramBottomBarForTabbarShow();
    }
    showView(target);
    const tabbar = document.querySelector('.app-tabbar');
    if (!leavingConsult && tabbar && target === 'view-main') {
      tabbar.classList.remove('app-tabbar-hidden');
      scheduleTelegramChromeColors();
    }
  });
});

const tabOrder = document.getElementById('tab-order');
const tabPortfolio = document.getElementById('tab-portfolio');
const tabProfile = document.getElementById('tab-profile');
const tabOwner = document.getElementById('tab-owner');
const allTabs = [tabOrder, tabPortfolio, tabProfile, tabOwner];

export function setActiveTab(tab: HTMLElement | null): void {
  allTabs.forEach((t) => {
    if (!t) return;
    if (t === tab) t.classList.add('tabbar-item-active');
    else t.classList.remove('tabbar-item-active');
  });
}

if (tabOrder) {
  tabOrder.addEventListener('click', () => {
    preemptTelegramBottomBarForTabbarShow();
    const current = document.querySelector('.view-active');
    const dir: ViewDirection =
      current &&
      (current.id === 'view-owner-presets' ||
        current.id === 'view-presets' ||
        current.id === 'view-portfolio' ||
        current.id === 'view-profile')
        ? 'left'
        : undefined;
    setActiveTab(tabOrder);
    showView('view-main', dir);
  });
}

function createSheetController(sheetId: string, backdropId: string, closeButtonIds: string[] = []): {
  open: () => void;
} {
  const sheet = document.getElementById(sheetId);
  const backdrop = document.getElementById(backdropId);
  let closeTimer: ReturnType<typeof setTimeout> | null = null;

  function close(): void {
    if (!sheet) return;
    if (closeTimer) {
      try {
        clearTimeout(closeTimer);
      } catch {
        /* ignore */
      }
      closeTimer = null;
    }
    sheet.classList.remove('sheet-open');
    sheet.classList.add('sheet-closing');
    closeTimer = setTimeout(() => {
      sheet.classList.remove('sheet-closing');
      sheet.setAttribute('aria-hidden', 'true');
      try {
        document.body.classList.remove('sheet-raise-app');
      } catch {
        /* ignore */
      }
      closeTimer = null;
    }, 320);
  }

  closeButtonIds.forEach((id) => {
    const btn = document.getElementById(id);
    if (btn) btn.addEventListener('click', close);
  });
  if (backdrop) backdrop.addEventListener('click', close);

  return {
    open(): void {
      if (!sheet) return;
      if (closeTimer) {
        try {
          clearTimeout(closeTimer);
        } catch {
          /* ignore */
        }
        closeTimer = null;
      }
      sheet.classList.remove('sheet-closing');
      sheet.classList.add('sheet-open');
      sheet.setAttribute('aria-hidden', 'false');
      try {
        document.body.classList.add('sheet-raise-app');
      } catch {
        /* ignore */
      }
    }
  };
}

(function initBugReportSheet(): void {
  const sheet = document.getElementById('bug-report-sheet');
  const backdrop = document.getElementById('bug-report-backdrop');
  const btnTelegram = document.getElementById('bug-report-telegram');
  let closeTimer: ReturnType<typeof setTimeout> | null = null;

  function close(): void {
    if (!sheet) return;
    if (closeTimer) {
      try {
        clearTimeout(closeTimer);
      } catch {
        /* ignore */
      }
      closeTimer = null;
    }
    sheet.classList.remove('sheet-open');
    sheet.classList.add('sheet-closing');
    closeTimer = setTimeout(() => {
      sheet.classList.remove('sheet-closing');
      sheet.setAttribute('aria-hidden', 'true');
      try {
        document.body.classList.remove('sheet-raise-app');
      } catch {
        /* ignore */
      }
      closeTimer = null;
    }, 320);
  }

  if (backdrop) backdrop.addEventListener('click', close);
  if (btnTelegram) {
    btnTelegram.addEventListener('click', () => {
      try {
        window.open('https://t.me/kluph', '_blank');
      } catch {
        /* ignore */
      }
      close();
    });
  }
})();

(function initAvitoSheet(): void {
  createSheetController('avito-unavailable-sheet', 'avito-unavailable-backdrop', [
    'avito-unavailable-ok'
  ]);
})();

if (tabPortfolio) {
  tabPortfolio.addEventListener('click', () => {
    if (!isCustomerPortfolioLive()) {
      openBetaWipSheet();
      return;
    }
    preemptTelegramBottomBarForTabbarShow();
    const current = document.querySelector('.view-active');
    const dir: ViewDirection =
      current &&
      (current.id === 'view-owner-presets' ||
        current.id === 'view-owner-presets-ready' ||
        current.id === 'view-owner-portfolio')
        ? 'left'
        : current?.id === 'view-profile'
          ? 'left'
          : current?.id === 'view-main'
            ? 'right'
            : undefined;
    setActiveTab(tabPortfolio);
    showView('view-portfolio', dir);
    bindPortfolioFilterBar();
    loadPublicPortfolio();
  });
}

if (tabProfile) {
  tabProfile.addEventListener('click', () => {
    preemptTelegramBottomBarForTabbarShow();
    const current = document.querySelector('.view-active');
    const dir: ViewDirection =
      current &&
      (current.id === 'view-owner-presets' ||
        current.id === 'view-owner-presets-ready' ||
        current.id === 'view-owner-portfolio')
        ? 'left'
        : current && (current.id === 'view-main' || current.id === 'view-portfolio')
          ? 'right'
          : undefined;
    setActiveTab(tabProfile);
    showView('view-profile', dir);
  });
}

if (tabOwner) {
  tabOwner.style.display = 'none';
}
